import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, UserPlus, Users, Ban, RotateCcw, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function SettingsPage() {
  const { user } = useAuth();
  const [mfa, setMfa] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "readonly" });
  const [resetTarget, setResetTarget] = useState<any>(null);
  const [newPw, setNewPw] = useState("");
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [deactivateTarget, setDeactivateTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [hideDeactivated, setHideDeactivated] = useState(false);
  const isAdmin = user?.role === "admin";

  const reloadUsers = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/auth/mfa").then((r) => setMfa(r.data));
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isAdmin]);

  const toggleMfa = async () => {
    const { data } = await api.post("/auth/mfa");
    setMfa({ ...mfa, enabled: data.enabled });
    toast.success(`MFA preference ${data.enabled ? "enabled" : "disabled"} (architecture-ready placeholder)`);
  };

  const createUser = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("All fields required"); return; }
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "readonly" });
      api.get("/users").then((r) => setUsers(r.data));
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submitReset = async () => {
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: newPw });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null); setNewPw("");
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setActive = async (u: any, active: boolean) => {
    try {
      await api.post(`/users/${u.id}/${active ? "reactivate" : "deactivate"}`);
      toast.success(`${u.email} ${active ? "reactivated" : "deactivated"}`);
      setDeactivateTarget(null);
      reloadUsers();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.email} deleted`);
      setDeleteTarget(null);
      reloadUsers();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="settings-page">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Settings</h1>

      <Card className="p-4 border-slate-200 shadow-none" data-testid="mfa-card">
        <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-teal-700" /> Multi-factor Authentication</h3>
        <p className="text-sm text-slate-500 mt-1">{mfa?.message}</p>
        <div className="flex items-center gap-3 mt-3">
          <Switch checked={!!mfa?.enabled} onCheckedChange={toggleMfa} data-testid="mfa-toggle" />
          <span className="text-sm text-slate-700">{mfa?.enabled ? "Enabled (placeholder)" : "Disabled"}</span>
          <span className="text-[11px] text-slate-400">Platform-native enrolment: {mfa?.available ? "available" : "not available this pass"}</span>
        </div>
      </Card>

      <Card className="p-4 border-slate-200 shadow-none" data-testid="change-password-card">
        <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><KeyRound className="h-4 w-4 text-teal-700" /> Change my password</h3>
        <p className="text-sm text-slate-500 mt-1">Update the password for your own account ({user?.email}).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs">Current password</Label>
            <Input type="password" data-testid="cp-current" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">New password (min 8 characters)</Label>
            <Input type="password" data-testid="cp-new" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} />
          </div>
        </div>
        <Button className="mt-3 bg-[#0B1C3D] hover:bg-[#122a55]" data-testid="cp-submit" onClick={async () => {
          if (pwForm.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
          try {
            await api.post("/auth/change-password", pwForm);
            toast.success("Password changed");
            setPwForm({ current_password: "", new_password: "" });
          } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
        }}>Update password</Button>
      </Card>

      {isAdmin && (
        <Card className="p-4 border-slate-200 shadow-none" data-testid="users-card">
          <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Users className="h-4 w-4" /> User Administration</h3>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <div><Label className="text-xs">Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs">Email</Label><Input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs">Password</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="readonly">Read-only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={createUser} data-testid="create-user"><UserPlus className="h-4 w-4 mr-1" /> Create</Button>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2 text-xs text-slate-500">
            <Switch checked={hideDeactivated} onCheckedChange={setHideDeactivated} data-testid="hide-deactivated-toggle" />
            <span>Hide deactivated users</span>
          </div>
          <div className="mt-2 divide-y divide-slate-100">
            {users.filter((u) => !(hideDeactivated && u.is_active === false)).map((u) => {
              const inactive = u.is_active === false;
              const isSelf = u.id === user?.id;
              return (
                <div key={u.id} className="flex items-center justify-between py-2 text-sm gap-2" data-testid={`user-row-${u.id}`}>
                  <div className="min-w-0">
                    <span className="font-medium text-slate-800">{u.name}</span> <span className="text-slate-400">· {u.email}</span>
                    {inactive && <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">Deactivated</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] uppercase tracking-wide text-teal-700 font-semibold">{u.role}</span>
                    {!inactive && (
                      <Button size="sm" variant="outline" onClick={() => { setResetTarget(u); setNewPw(""); }} data-testid={`reset-pw-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
                      </Button>
                    )}
                    {inactive ? (
                      <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => setActive(u, true)} data-testid={`reactivate-${u.id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivate
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={isSelf} title={isSelf ? "You cannot deactivate your own account" : ""} className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40" onClick={() => setDeactivateTarget(u)} data-testid={`deactivate-${u.id}`}>
                        <Ban className="h-3.5 w-3.5 mr-1" /> Deactivate
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={isSelf} title={isSelf ? "You cannot delete your own account" : ""} className="text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-40" onClick={() => setDeleteTarget(u)} data-testid={`delete-${u.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setNewPw(""); } }}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for <span className="font-medium">{resetTarget?.email}</span>. They should change it after signing in. This is logged to the audit trail.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">New password (min 8 characters)</Label>
            <Input type="password" data-testid="reset-pw-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setNewPw(""); }}>Cancel</Button>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={submitReset} data-testid="reset-pw-submit">Set password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deactivateTarget} onOpenChange={(v) => { if (!v) setDeactivateTarget(null); }}>
        <DialogContent data-testid="deactivate-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate user</DialogTitle>
            <DialogDescription>
              <span className="font-medium">{deactivateTarget?.email}</span> will no longer be able to sign in. You can reactivate them later. This is logged to the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setActive(deactivateTarget, false)} data-testid="deactivate-confirm">Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete user permanently</DialogTitle>
            <DialogDescription>
              <span className="font-medium">{deleteTarget?.email}</span> will be permanently removed. This cannot be undone. Consider deactivating instead if you may need the account again. This is logged to the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={deleteUser} data-testid="delete-confirm">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-4 border-slate-200 shadow-none">
        <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><KeyRound className="h-4 w-4" /> Signed in as</h3>
        <div className="text-sm text-slate-600 mt-1">{user?.name} · {user?.email} · <span className="uppercase text-teal-700 font-semibold">{user?.role}</span></div>
      </Card>
    </div>
  );
}
